/* eslint-disable camelcase */
import * as THREE from 'three'
import { getLineMaterial, getSeatMaterial } from '../utils/helper_functions'
import { drawLabel } from './drawLabel'
import { drawTableHalfCircle } from './drawTableHalfCircle'
import { COLORS, OBJECT_TYPES, SEAT_STATUSES, SEAT_TYPES } from '../constants'
import { seats, textGeometries, materials } from '../utils/mapData'

export const drawTable2Seats = (seatId, options) => {
  const {
    color,
    position,
    rotation,
    tierId,
    parent,
    center_x,
    center_y,
    scale,
    seat_circle_radius,
    events,
    sectionLabel,
    rowLabel,
    label,
    fonts,
    disabled = false,
    type = SEAT_TYPES.CHAIR,
    faceTo = 'left',
    enableTicketTypeAssign,
    tierColor,
    selectedTierId,
    sceneMeshes
  } = options

  let { opacity = 1 } = options

  const seatColor = enableTicketTypeAssign ? tierColor : COLORS.BOOKED

  position[0] -= center_x * scale
  position[1] += center_y * scale

  if (selectedTierId && selectedTierId !== tierId) {
    opacity = 0.3
  }

  const radius = (seat_circle_radius || 14 - 2) * scale
  const seat = new THREE.Group()

  seat.position.set(...position)

  const circleGeometry = new THREE.CircleGeometry(radius, 32)
  const circleMaterial = getSeatMaterial(
    { color: seatColor, opacity },
    materials
  )

  const circle1 = new THREE.Mesh(circleGeometry, circleMaterial)
  const circle2 = new THREE.Mesh(circleGeometry, circleMaterial)

  const circleBorder1 = new THREE.LineSegments(
    new THREE.EdgesGeometry(circleGeometry),
    getLineMaterial({ color: COLORS.BOOKED, opacity }, materials)
  )

  const circleBorder2 = circleBorder1.clone()

  const sign = faceTo === 'left' ? 1 : -1

  const tableRadius = 2.5 * radius
  const c1p = new THREE.Vector3(sign * (0.2 - tableRadius), 0, 0).sub(
    seat.position
  )

  const c2p = new THREE.Vector3(
    (sign * (0.2 - tableRadius)) / 2,
    ((0.2 - tableRadius) * Math.sqrt(3)) / 2,
    0
  ).sub(seat.position)

  circle1.position.copy(c1p)
  circleBorder1.position.copy(c1p)
  circle2.position.copy(c2p)
  circleBorder2.position.copy(c2p)

  const [triangle] = drawTableHalfCircle(
    seat.position,
    faceTo,
    seatColor,
    tableRadius,
    materials,
    sceneMeshes
  )

  seat.add(triangle, circle1, circle2, circleBorder1, circleBorder2)

  seat.isRequestPending = false
  seat.objectType = OBJECT_TYPES.SEAT
  seat.opacity = opacity
  seat.disabled = disabled
  seat.allowColorUpdateByStatus = true
  seat.radius = radius
  seat.seatId = seatId
  seat.tierId = tierId
  seat.status = SEAT_STATUSES.BOOKED
  seat.sectionLabel = sectionLabel
  seat.rowLabel = rowLabel
  seat.label = label
  seat.seatType = type
  seat.color = seatColor

  seat.material = triangle.obj.material // this is necessary for showing color in label

  seat.changeBorderColor = (color) => {
    circleBorder1.material =
      circleBorder2.material =
      triangle.border.material =
        getLineMaterial({ color }, materials)
  }

  seat.mouseOver = seat.disabled ? null : events.onMouseOver
  seat.mouseOut = seat.disabled ? null : events.onMouseOut

  seat.changeBorderColorToDefault = () => {
    seat.changeBorderColor(COLORS.BOOKED)
  }

  seat.changeColor = (color, opacity = 1) => {
    seat.changeBorderColor(color)

    circle1.material = circle2.material = getSeatMaterial(
      {
        color,
        opacity
      },
      materials
    )
    triangle.obj.material = getSeatMaterial({ color, opacity }, materials)
    seat.changeBorderColorToDefault = () => {
      seat.changeBorderColor(color)
    }
    seat.material = triangle.obj.material
  }

  const makeAvailable = () => {
    seat.status = SEAT_STATUSES.AVAILABLE
  }

  const makeHold = () => {
    seat.status = SEAT_STATUSES.HOLD
  }

  const makeReserved = () => {
    seat.status = SEAT_STATUSES.RESERVED
  }

  const makeBooked = () => {
    seat.status = SEAT_STATUSES.BOOKED
  }

  const makeBlocked = () => {
    seat.status = SEAT_STATUSES.BLOCKED
  }

  const makeReservedByMe = () => {
    window.localStorage.setItem('tierId', tierId)
    seat.status = SEAT_STATUSES.RESERVED_BY_ME
  }

  seat.setColorByStatus = () => {
    if (!seat.allowColorUpdateByStatus) {
      return
    }

    switch (seat.status) {
      case SEAT_STATUSES.AVAILABLE: {
        seat.changeColor(color)
        break
      }
      case SEAT_STATUSES.HOLD: {
        seat.changeColor(COLORS.HOLD)
        break
      }
      case SEAT_STATUSES.RESERVED_BY_ME: {
        seat.changeColor(COLORS.RESERVED_BY_ME)
        break
      }
      case SEAT_STATUSES.RESERVED: {
        seat.changeColor(COLORS.RESERVED)
        break
      }
      case SEAT_STATUSES.BOOKED: {
        seat.changeColor(COLORS.BOOKED)
        break
      }
      case SEAT_STATUSES.BLOCKED: {
        seat.changeColor(COLORS.BLOCKED)
        break
      }
    }
  }

  seat.changeOpacity = (opacity, color) => {
    seat.changeColor(color, opacity)
  }

  seat.makeDisabled = () => {
    if (seat.disabled) {
      return
    }
    seat.disabled = true
    seat.mouseOver = null
    seat.mouseOut = null
    seat.opacity = 0.3
    seat.changeOpacity(0.3, seatColor)
  }

  seat.makeActive = () => {
    if (!seat.disabled) {
      return
    }
    seat.disabled = false
    seat.mouseOver = events.onMouseOver
    seat.mouseOut = events.onMouseOut
    seat.opacity = 1
    seat.changeOpacity(1, seatColor)
  }

  const select = () => {
    circleBorder1.material =
      circleBorder2.material =
      triangle.border.material =
        getLineMaterial(
          {
            color: '#575656',
            linewidth: 5,
            opacity: seat.opacity
          },
          materials
        )
    seat.changeBorderColor = () => null
  }

  const unselect = () => {
    circleBorder1.material =
      circleBorder2.material =
      triangle.border.material =
        getLineMaterial({ color: '#000', opacity: seat.opacity }, materials)
    seat.changeBorderColor = (color) => {
      circleBorder1.material =
        circleBorder2.material =
        triangle.border.material =
          getLineMaterial({ color, opacity: seat.opacity }, materials)
    }
    seat.changeBorderColorToDefault()
  }

  seat.select = select
  seat.unselect = unselect

  seat.unsetLoading = () => null
  seat.setLoading = () => {
    if (window.isMobile) {
      // is mobile
      const label = drawLabel(
        '\uF110',
        {
          font: fonts.fontAwesome,
          color: 'grey',
          position: [0, 0, 0],
          center_x: 0,
          center_y: 0,
          scale,
          seat_circle_radius,
          scene: seat,
          size: seat_circle_radius * scale,
          rotation
        },
        textGeometries,
        materials
      )
      seat.unsetLoading = () => seat.remove(label)
    }
  }

  seat.statusUpdate = (status) => {
    if (seat.status !== status) {
      switch (status) {
        case SEAT_STATUSES.PERMANENT_RESERVED:
        case SEAT_STATUSES.AVAILABLE: {
          makeAvailable()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.HOLD: {
          makeHold()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.RESERVED_BY_ME: {
          makeReservedByMe()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.RESERVED: {
          makeReserved()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.BOOKED: {
          makeBooked()
          seat.unsetLoading()
          break
        }
        case SEAT_STATUSES.BLOCKED: {
          makeBlocked()
          seat.unsetLoading()
          break
        }
      }

      seat.setColorByStatus()
    }
  }

  seats[seatId] = seat
  seat.changeOpacity(opacity, seatColor)
  seat.hasHandlers = true
  sceneMeshes.push(seat)
  parent.add(seat)
}
